import { Button, Empty, Input, Radio, Select, Space, Tag, Tooltip, Typography } from 'antd';
import React from 'react';
import ReactMarkdown from 'react-markdown';

import {
  useChatFooter,
  providerRequiresModel,
  type LlmProviderAgentDTO,
  type LlmProviderKey,
} from '@/hooks/useChatFooter';
import { OutputIcon as FileTextOutlined, SaveIcon as SaveOutlined } from '@/icons';

const { Paragraph, Text, Title } = Typography;

const providerLabelMap: Record<LlmProviderKey, string> = {
  claude: 'Claude',
  codex: 'Codex',
  copilot: 'CoPilot',
  cursor: 'Cursor',
  aws_bedrock_platform: 'Bedrock: Platform',
  aws_bedrock: 'Bedrock',
};
const bedrockProviderKeys = new Set<LlmProviderKey>(['aws_bedrock', 'aws_bedrock_platform']);

const isBedrockProvider = (provider: LlmProviderKey): boolean => bedrockProviderKeys.has(provider);
const getProviderDisplayLabel = (provider: LlmProviderKey): string =>
  provider === 'aws_bedrock_platform'
    ? providerLabelMap[provider]
    : `${providerLabelMap[provider]}: BYO`;

/** Props for the `ChatFooterLLMSwitcher` floating chat panel and LLM provider selector. */
export interface ChatFooterLLMSwitcherProps {
  accessToken: string;
  clientId: string;
  projectId: string;
  onCreateNoteNavigate?: (assetSessionId: string) => void;
  chatPanelOpen?: boolean;
  onChatPanelOpenChange?: (open: boolean) => void;
}

/**
 * Floating chat side panel that lets users select an LLM provider/model, send messages,
 * view streaming responses with citations, and save answers as knowledge notes.
 */
export const ChatFooterLLMSwitcher: React.FC<ChatFooterLLMSwitcherProps> = ({
  accessToken,
  clientId,
  projectId,
  onCreateNoteNavigate,
  chatPanelOpen,
  onChatPanelOpenChange,
}) => {
  const {
    selectedProvider,
    selectedModel,
    connectedProviders,
    selectedProviderModels,
    selectedAgentLabel,
    modelSelectPlaceholder,
    isLoading,
    isSaving,
    isSending,
    chatPhase,
    isPanelOpen,
    chatDraft,
    chatHistory,
    summaryDetail,
    savedNoteIds,
    savingNoteId,
    canSend,
    setChatDraft,
    setSummaryDetail,
    setPanelOpen,
    ensureModelsLoaded,
    onProviderChange,
    onModelChange,
    onSend,
    onSaveAsNote,
  } = useChatFooter(
    accessToken,
    clientId,
    projectId,
    chatPanelOpen,
    onChatPanelOpenChange,
    onCreateNoteNavigate,
  );

  return (
    <>
      <div className="chat-footer-switcher" data-loading={isLoading ? 'true' : 'false'}>
        <div className="chat-footer-switcher__single-row">
          <Select<LlmProviderKey>
            size="small"
            data-testid="chat-provider-select"
            className="chat-footer-switcher__provider-select"
            value={selectedProvider ?? undefined}
            options={connectedProviders.map((item) => ({
              value: item.provider,
              label: getProviderDisplayLabel(item.provider),
            }))}
            placeholder="Provider"
            onChange={(value) => void onProviderChange(value)}
            disabled={isSaving || isSending || isLoading || connectedProviders.length === 0}
          />

          <Select<string>
            size="small"
            data-testid="chat-model-select"
            className="chat-footer-switcher__model-select"
            value={selectedModel ?? undefined}
            options={selectedProviderModels.map((item: LlmProviderAgentDTO) => ({
              value: item.agent_id,
              label: item.label,
            }))}
            placeholder={modelSelectPlaceholder}
            notFoundContent={
              selectedProvider && isBedrockProvider(selectedProvider)
                ? 'No models match active profiles'
                : 'No models available'
            }
            allowClear={!!selectedProvider && !providerRequiresModel(selectedProvider)}
            onFocus={() => {
              if (selectedProvider) {
                void ensureModelsLoaded(selectedProvider);
              }
            }}
            onChange={(value) => void onModelChange(value ?? null)}
            disabled={isSaving || isSending || isLoading || !selectedProvider}
          />
        </div>
      </div>

      <aside
        className={`chat-side-panel${isPanelOpen ? ' chat-side-panel--open' : ''}`}
        aria-hidden={!isPanelOpen}
      >
        <div className="chat-side-panel__header">
          <Title level={5} className="chat-side-panel__title">
            Project Chat
          </Title>
          <Button type="text" size="small" onClick={() => setPanelOpen(false)}>
            Close
          </Button>
        </div>

        <div className="chat-drawer">
          {selectedProvider ? (
            <>
              <Paragraph className="chat-drawer__context">
                Using: {getProviderDisplayLabel(selectedProvider)}
                {selectedAgentLabel ? ` · ${selectedAgentLabel}` : ''}
              </Paragraph>
              {isBedrockProvider(selectedProvider) ? (
                <Paragraph className="chat-drawer__context" type="secondary">
                  Bedrock model options are filtered by active inference profiles.
                </Paragraph>
              ) : null}
            </>
          ) : null}

          <div className="chat-drawer__messages">
            {chatHistory.length === 0 ? (
              <Empty description="Start a conversation with your selected provider." />
            ) : (
              chatHistory.map((item) => (
                <div
                  key={item.id}
                  className={`chat-drawer__message chat-drawer__message--${item.role}`}
                >
                  {item.role === 'assistant' ? (
                    <div className="chat-drawer__markdown">
                      <ReactMarkdown>{item.text}</ReactMarkdown>
                    </div>
                  ) : (
                    <Text>{item.text}</Text>
                  )}
                  {item.role === 'assistant' && item.citations && item.citations.length > 0 && (
                    <div className="chat-drawer__citations" style={{ marginTop: 8 }}>
                      <Text
                        type="secondary"
                        style={{ fontSize: 12, display: 'block', marginBottom: 4 }}
                      >
                        Sources:
                      </Text>
                      <Space size={[4, 4]} wrap>
                        {item.citations.map((citation) => (
                          <Tag
                            key={citation.card_id}
                            icon={<FileTextOutlined />}
                            color="blue"
                            style={{ cursor: 'pointer', fontSize: 11 }}
                            onClick={() => {
                              /* Navigate to asset — integration point for onOpenAsset */
                            }}
                          >
                            {citation.title}
                          </Tag>
                        ))}
                      </Space>
                    </div>
                  )}
                  {item.role === 'assistant' && (
                    <div className="chat-drawer__message-actions">
                      <Tooltip
                        title={
                          savedNoteIds.has(item.id) ? 'Saved to Asset Library' : 'Save as Note'
                        }
                      >
                        <Button
                          type="text"
                          size="small"
                          icon={<SaveOutlined />}
                          disabled={savedNoteIds.has(item.id)}
                          loading={savingNoteId === item.id}
                          onClick={() => void onSaveAsNote(item)}
                          className="chat-drawer__save-note-btn"
                        >
                          {savedNoteIds.has(item.id) ? 'Saved' : 'Save as Note'}
                        </Button>
                      </Tooltip>
                    </div>
                  )}
                </div>
              ))
            )}
            {isSending && (
              <div className="chat-drawer__message chat-drawer__message--assistant">
                <Text type="secondary" style={{ fontStyle: 'italic' }}>
                  {chatPhase === 'retrieving' ? 'Searching your assets...' : 'Thinking...'}
                </Text>
              </div>
            )}
          </div>

          <div className="chat-drawer__composer">
            <Input.TextArea
              value={chatDraft}
              placeholder="Type a chat message..."
              autoSize={{ minRows: 3, maxRows: 5 }}
              onChange={(event) => setChatDraft(event.target.value)}
              disabled={isSending}
            />
            <div className="chat-drawer__composer-actions">
              <Radio.Group
                size="small"
                value={summaryDetail}
                onChange={(event) => setSummaryDetail(event.target.value)}
                optionType="button"
                buttonStyle="solid"
                data-testid="summary-detail-toggle"
              >
                <Radio.Button value="concise">Concise</Radio.Button>
                <Radio.Button value="detailed">Detailed</Radio.Button>
              </Radio.Group>
              <Button
                type="primary"
                disabled={!canSend}
                loading={isSending}
                onClick={() => void onSend()}
              >
                Send
              </Button>
            </div>
          </div>
        </div>
      </aside>
    </>
  );
};

export default ChatFooterLLMSwitcher;
